import { CopyOutlined, LinkOutlined } from "@ant-design/icons";
import { useSearch } from "@tanstack/react-router";
import { Alert, Button, Select, Typography } from "antd";
import { useGetFormStructureQuery } from "api/TenantSlug/FormStructure/hooks.ts";
import { useGetOfferQuery } from "api/TenantSlug/Offer/hooks";
import { Drawer } from "components/UI/Drawer";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { QueryState } from "components/UI/QueryState/QueryState.tsx";
import { TextArea } from "components/UI/TextArea/TextArea.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser.ts";
import { FC, useEffect, useMemo, useState } from "react";
import AceEditor from "react-ace";
import { toast } from "react-toastify";
import { DrawerId } from "types/models.ts";

import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-github";

const { Text, Link, Title } = Typography;

export const OfferApiDocumentationDrawer: FC = () => {
    const { t } = usePaldockTranslation("drawers.offer-api-documentation");
    const { t: tC } = usePaldockTranslation("common");
    const id = DrawerId.OfferApiDocumentation;
    const userUtils = useUser();

    const params: { doc_s?: string; doc_o?: string } = useSearch({ strict: false });

    const {
        data: offerData,
        isLoading: isOfferLoading,
        isError: isOfferError,
    } = useGetOfferQuery(params.doc_o ?? "", { filter: { include: "formStructures" } }, { enabled: !!params.doc_o });
    const offer = offerData?.offer;

    const tenantSlug = userUtils.getActiveTenant()?.slug_name;
    const baseApiUrl = `${import.meta.env.VITE_API_URL}/${tenantSlug}`;
    const documentationUrl = `${baseApiUrl}/documentation/api/#/operations/conversions.api`;

    const formStructures = offer?.form_structures ?? [];
    const [selectedStructureId, setSelectedStructureId] = useState<string | null>(params.doc_s ?? null);

    useEffect(() => {
        setSelectedStructureId(params.doc_s ?? null);
    }, [params.doc_s]);

    const currentStructureId = selectedStructureId ?? params.doc_s ?? formStructures[0]?.id;

    // Fetch form structure with formFields included
    const formStructureQuery = useGetFormStructureQuery(
        currentStructureId ?? "",
        { filter: { include: "formFields" } },
        { enabled: !!currentStructureId && !!params.doc_s }
    );

    const selectedStructure = formStructureQuery.data?.formStructure;

    const endpointUrl = useMemo(() => {
        if (!params.doc_o || !currentStructureId) return "";
        return `${baseApiUrl}/conversions/api?o=${params.doc_o}&structure=${currentStructureId}`;
    }, [baseApiUrl, params.doc_o, currentStructureId]);

    const exampleJson = useMemo(() => {
        if (!selectedStructure?.formFields) return "{}";

        const example: Record<string, string | number | boolean | null> = {};
        for (const field of selectedStructure.formFields) {
            example[field.system_name] = field.example_value ?? field.default_value ?? "";
        }
        return JSON.stringify(example, null, 2);
    }, [selectedStructure]);

    const hasStructure = !!params.doc_s;
    const endpointMethod = "POST";

    return (
        <Drawer drawerId={id} title={<DrawerTitle mainTitle={t("title")} />} searchParamsToClean={["doc_s", "doc_o"]}>
            <QueryState isLoading={isOfferLoading} isError={isOfferError} errorKey="errors.loading_offer">
                <div className="flex flex-col gap-6">
                    <Alert
                        type="info"
                        showIcon
                        icon={<LinkOutlined />}
                        message={t("full_documentation")}
                        description={
                            <Link href={documentationUrl} target="_blank" strong>
                                {t("open_documentation")}
                            </Link>
                        }
                    />

                    {!hasStructure && <Alert type="warning" showIcon message={t("no_structure_available")} />}

                    {hasStructure && (
                        <>
                            <div>
                                <Title level={5}>
                                    {t("endpoint_label")} ({endpointMethod})
                                </Title>
                                <TextArea name="endpoint" appearance="readonly" readOnly rows={2} value={endpointUrl} />
                            </div>

                            {formStructures.length > 1 && (
                                <div>
                                    <Title level={5}>{t("select_structure")}</Title>
                                    <Select
                                        className="w-full"
                                        value={currentStructureId ?? undefined}
                                        onChange={setSelectedStructureId}
                                        options={formStructures.map((s) => ({
                                            label: s.name,
                                            value: s.id,
                                        }))}
                                    />
                                </div>
                            )}

                            <div>
                                <Title level={5}>{t("example_structure")}</Title>
                                <Text type="secondary" className="block mb-2">
                                    {t("structure_description", {
                                        name:
                                            selectedStructure?.name ??
                                            formStructures.find((s) => s.id === currentStructureId)?.name ??
                                            "",
                                    })}
                                </Text>
                                {formStructureQuery.isLoading || formStructureQuery.isError ? (
                                    <QueryState
                                        isLoading={formStructureQuery.isLoading}
                                        isError={formStructureQuery.isError}
                                    />
                                ) : (
                                    <div className="relative">
                                        <Button
                                            icon={<CopyOutlined />}
                                            size="small"
                                            className="absolute top-2 right-2 z-10"
                                            aria-label={tC("copy")}
                                            title={tC("copy")}
                                            onClick={async () => {
                                                try {
                                                    await navigator.clipboard.writeText(exampleJson);
                                                    toast.success(tC("copied"));
                                                } catch {
                                                    toast.error(tC("copy_failed"));
                                                }
                                            }}
                                        />
                                        <AceEditor
                                            name="api-doc-structure"
                                            mode="json"
                                            theme="github"
                                            value={exampleJson}
                                            readOnly
                                            width="100%"
                                            minLines={10}
                                            maxLines={30}
                                            wrapEnabled
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </QueryState>
        </Drawer>
    );
};
